import { infiniteQueryOptions, queryOptions } from "@tanstack/react-query";

import { useApi } from "@/api/hooks/useApi";
import { commonAPIDataSelector } from "@/helpers/Network/selectors";
import { useProjectId } from "@/hooks/Network/useProjectId";
import { QUERY_KEYS } from "@/query-keys";
import type { ApiQueryParams } from "@/types/api-types";

export const useNotificationQueries = () => {
  const api = useApi();
  const projectId = useProjectId();

  return {
    adminStatus: (options?: { staleTime?: number; refetchInterval?: number }) =>
      queryOptions({
        queryKey: QUERY_KEYS.ADMIN_NOTIFICATION_STATUS(projectId),
        queryFn: () => api.getAdminNotificationsStatusV1(),
        select: commonAPIDataSelector,
        staleTime: options?.staleTime ?? 30 * 1000,
        refetchInterval: options?.refetchInterval,
      }),

    adminListInfinite: (query: ApiQueryParams<"getAdminNotificationsV1">) => {
      const limit = 40;

      return infiniteQueryOptions({
        queryKey: QUERY_KEYS.ADMIN_NOTIFICATION_LIST(projectId, query),
        queryFn: ({ pageParam = 0 }) =>
          api
            .getAdminNotificationsV1({
              ...query,
              Offset: pageParam * limit,
              Limit: limit,
            })
            .then((items) => commonAPIDataSelector(items)),
        initialPageParam: 0,
        getNextPageParam: (lastPage, pages) => (lastPage?.hasMore ? pages?.length : undefined),
        staleTime: 30 * 1000,
        gcTime: 30 * 1000,
      });
    },

    residentStatus: (options?: { staleTime?: number; refetchInterval?: number }) =>
      queryOptions({
        queryKey: QUERY_KEYS.RESIDENT_NOTIFICATION_STATUS(projectId),
        queryFn: () => api.getResidentNotificationsStatusV2(),
        select: commonAPIDataSelector,
        staleTime: options?.staleTime ?? 30 * 1000,
        refetchInterval: options?.refetchInterval,
      }),

    residentListInfinite: (query: ApiQueryParams<"getResidentNotificationsV3"> = {}) => {
      const limit = 40;

      return infiniteQueryOptions({
        queryKey: QUERY_KEYS.RESIDENT_NOTIFICATION_LIST(projectId, query),
        queryFn: ({ pageParam = 0 }) =>
          api
            .getResidentNotificationsV3({
              ...query,
              Offset: pageParam * limit,
              Limit: limit,
            })
            .then(commonAPIDataSelector),
        initialPageParam: 0,
        getNextPageParam: (lastPage, pages) => (lastPage.notifications.hasMore ? pages.length : undefined),
        staleTime: 30 * 1000,
        gcTime: 30 * 1000,
      });
    },
  };
};
